import React, { useMemo, useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, Settings, Trash2, UserPlus } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Users tab in the Admin Panel.
 *
 * Super admins can:
 *   • Manage a user's partner-entity assignments (Entities button)
 *   • Change a user's role / admin status (Role button)
 *   • Delete a single user (Trash icon per row)
 *   • Bulk-delete multiple users via row checkboxes + "Delete selected" toolbar
 *
 * Deletion is a hard delete on the backend with cascade — see
 * `admin_delete_user` in routes/admin.py. Guardrails there (cannot self-delete,
 * cannot delete another super admin) mean the UI can be permissive with
 * selection — the backend will report `skipped` reasons if a row is refused.
 */
const UsersTab = ({
  users,
  currentUser,
  onOpenUserEntitiesModal,
  onOpenUserRoleModal,
  onDeleteUser,
  onBulkDeleteUsers,
  onOpenAddUserDialog,
}) => {
  const isSuperAdmin = currentUser?.admin_role === 'super_admin';
  const isAnyAdmin = !!currentUser?.is_admin;
  const [selected, setSelected] = useState(() => new Set());
  const [pendingDelete, setPendingDelete] = useState(null); // { mode: 'single' | 'bulk', ids: string[], label: string }

  // Filter out users the backend would reject anyway — the self-row and other
  // super admins — so the "select all" checkbox doesn't lie about the count.
  const selectableIds = useMemo(
    () =>
      users
        .filter((u) => u.user_id !== currentUser?.user_id && u.admin_role !== 'super_admin')
        .map((u) => u.user_id),
    [users, currentUser?.user_id],
  );

  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(selectableIds) : new Set());
  };

  const toggleOne = (userId, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  const askDeleteSingle = (u) => {
    setPendingDelete({
      mode: 'single',
      ids: [u.user_id],
      label: `${u.name || u.email} (${u.email})`,
    });
  };

  const askDeleteBulk = () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setPendingDelete({
      mode: 'bulk',
      ids,
      label: `${ids.length} user(s)`,
    });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    if (pendingDelete.mode === 'single') {
      await onDeleteUser?.(pendingDelete.ids[0]);
    } else {
      await onBulkDeleteUsers?.(pendingDelete.ids);
      setSelected(new Set());
    }
    setPendingDelete(null);
  };

  return (
    <TabsContent value="users" className="space-y-6">
      {/* Toolbar — Add User is visible to every admin. Backend enforces
          scope: entity admins can only attach to partners they administer,
          super admins can attach to anything. */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {isSuperAdmin
            ? `${users.length} user${users.length === 1 ? '' : 's'} across all partners`
            : `${users.length} user${users.length === 1 ? '' : 's'} in your organization`}
        </div>
        {isAnyAdmin && (
          <Button size="sm" onClick={onOpenAddUserDialog} data-testid="add-user-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        )}
      </div>

      {isSuperAdmin && selected.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3"
          data-testid="users-bulk-toolbar"
        >
          <span className="text-sm">
            <strong>{selected.size}</strong> user{selected.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
              data-testid="users-bulk-clear"
            >
              Clear
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={askDeleteBulk}
              data-testid="users-bulk-delete-btn"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Delete selected
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {isSuperAdmin && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select all users"
                    disabled={selectableIds.length === 0}
                    data-testid="users-select-all"
                  />
                </TableHead>
              )}
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Badges</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 7 : 6} className="text-center py-8 text-muted-foreground">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => {
                const isSelf = u.user_id === currentUser?.user_id;
                const isProtected = isSelf || u.admin_role === 'super_admin';
                return (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    {isSuperAdmin && (
                      <TableCell>
                        <Checkbox
                          checked={selected.has(u.user_id)}
                          onCheckedChange={(v) => toggleOne(u.user_id, !!v)}
                          disabled={isProtected}
                          aria-label={`Select ${u.name}`}
                          data-testid={`user-select-${u.user_id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {u.picture && (
                          <img
                            src={u.picture}
                            alt={u.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="capitalize">{u.role}</TableCell>
                    <TableCell>
                      {u.is_admin ? (
                        <Badge className="bg-primary/20 text-primary border-0 capitalize">
                          {u.admin_role?.replace('_', ' ') || 'Admin'}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>{(u.badges || []).length}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isSuperAdmin && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenUserEntitiesModal(u)}
                              title="Manage Partners"
                              data-testid={`manage-entities-${u.user_id}`}
                            >
                              <Building2 className="h-3 w-3 mr-1" />
                              Entities
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenUserRoleModal(u)}
                              data-testid={`manage-${u.user_id}`}
                            >
                              <Settings className="h-3 w-3 mr-1" />
                              Role
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => askDeleteSingle(u)}
                              disabled={isProtected}
                              title={
                                isSelf
                                  ? 'You cannot delete your own account'
                                  : u.admin_role === 'super_admin'
                                    ? 'Demote this super admin first'
                                    : 'Delete user'
                              }
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {isSelf && (
                          <span className="text-xs text-muted-foreground">Current User</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete?.mode === 'bulk' ? 'Delete selected users?' : 'Delete this user?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{pendingDelete?.label}</strong> along with their
              saved routes, reviews, badge history, push tokens, and sessions. Their entity-admin
              assignments will be unlinked. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-user-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TabsContent>
  );
};

export default UsersTab;
